<script setup lang="ts">
/**
 * 导出面板：一处收齐所有导出（SVG / PNG / JPEG / PDF / 工程）。
 *
 * 设计取舍：
 *  · 状态栏在底部 → 面板**向上展开**；teleport 到 body 避免被状态栏裁切。
 *  · 尺寸两种入口：**倍率**（相对 96dpi 的放大）与 **物理尺寸 × DPI**（期刊投稿要的规格）。
 *    两者换算都走 `planRaster`（纯函数、有单测），面板只负责显示与回读。
 *  · 面板里显示**将要生成的文件名**与像素尺寸，导出前就能核对，不用下完再看。
 *  · PDF 是**矢量**：只有页面盒（图幅 / A4）要选，没有倍率与 DPI —— 矢量放大不糊。
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useEditor } from '@/composables/useEditor';
import { MAX_RASTER_PX, planRaster, rasterFileName } from '@/core/io/raster';
import { pdfFileName, type PdfPage } from '@/core/io/pdf';
import { viewBoxRect } from '@/core/geom/viewport';
import { ui } from '@/stores/editor';
import SliderField from './Inspector/fields/SliderField.vue';

type ExportFormat = 'svg' | 'png' | 'jpeg' | 'pdf' | 'project';
type RasterKind = 'png' | 'jpeg';

const editor = useEditor();
const format = ref<ExportFormat>('svg');
const sizeMode = ref<'scale' | 'dpi'>('scale');
const scale = ref(2);
const dpi = ref(300);
const mmWidth = ref(170);
const transparent = ref(true);
const bgColor = ref('#ffffff');
const quality = ref(0.92);
/** PDF 页面规格：按图幅（默认）/ A4 竖 / A4 横 */
const pdfPage = ref<PdfPage>('content');
const busy = ref(false);
const message = ref('');
const failed = ref(false);
const style = ref<Record<string, string>>({ left: '0px', bottom: '0px' });

const PANEL_W = 306;

const box = computed(() => {
  void editor.version.value;
  const root = editor.svg.value;
  return root ? viewBoxRect(root) : null;
});

const plan = computed(() =>
  planRaster({
    width: box.value?.w ?? 1,
    height: box.value?.h ?? 1,
    scale: sizeMode.value === 'scale' ? scale.value : undefined,
    dpi: sizeMode.value === 'dpi' ? dpi.value : undefined,
    mmWidth: sizeMode.value === 'dpi' ? mmWidth.value : undefined,
  }),
);

/** 位图格式（带类型收窄：不给外面留 `as 'png'|'jpeg'` 这种硬断言的机会） */
const rasterFormat = computed<RasterKind | null>(() =>
  format.value === 'png' || format.value === 'jpeg' ? format.value : null,
);
const isJpeg = computed(() => format.value === 'jpeg');
const isPdf = computed(() => format.value === 'pdf');
/** JPEG 必须不透明（没有 alpha 通道）；PNG 可由用户选透明或底色 */
const bgValue = computed<string | null>(() => {
  if (isJpeg.value) return bgColor.value;
  return transparent.value ? null : bgColor.value;
});

/** 目标像素尺寸文案 */
const sizeText = computed(() => `${plan.value.pixelWidth} × ${plan.value.pixelHeight} px`);
/** 印刷尺寸（mm）与"含多少 dpi"——倍率模式下也换算出来，便于对照投稿要求 */
const physicalText = computed(() => {
  const b = box.value;
  if (!b) return '';
  const mm = sizeMode.value === 'dpi' ? mmWidth.value : (plan.value.pixelWidth / 96) * 25.4;
  return `${mm.toFixed(1)} mm 宽（约 ${Math.round(plan.value.pixelWidth / (mm / 25.4))} dpi）`;
});
const fileName = computed(() => {
  if (isPdf.value) return pdfFileName(editor.sourceName.value);
  const kind = rasterFormat.value;
  return kind ? rasterFileName(editor.sourceName.value, plan.value, kind) : '';
});

const formatLabel: Record<ExportFormat, string> = {
  svg: 'SVG（矢量成品）',
  png: 'PNG（位图，可透明）',
  jpeg: 'JPEG（位图，不可透明）',
  pdf: 'PDF（矢量，打印/投稿）',
  project: '工程（.svgedit.json，可续编）',
};

const PDF_PAGES: ReadonlyArray<{ id: PdfPage; label: string; hint: string }> = [
  { id: 'content', label: '按图幅', hint: '页面尺寸 = 图幅，无白边' },
  { id: 'a4', label: 'A4 竖', hint: '内容等比居中，四周留 10mm' },
  { id: 'a4l', label: 'A4 横', hint: '内容等比居中，四周留 10mm' },
];
const pdfHint = computed(() => PDF_PAGES.find((p) => p.id === pdfPage.value)?.hint ?? '');

const position = (): void => {
  const anchor = document.querySelector('[data-field="export-open"]');
  if (!anchor) return;
  const r = anchor.getBoundingClientRect();
  const left = Math.max(8, Math.min(r.right - PANEL_W, window.innerWidth - PANEL_W - 8));
  style.value = { left: `${Math.round(left)}px`, bottom: `${Math.round(window.innerHeight - r.top + 8)}px` };
};

const close = (): void => {
  ui.exportOpen = false;
};

const onDocDown = (e: Event): void => {
  const t = e.target;
  if (t instanceof Element && (t.closest('.export-panel') || t.closest('[data-field="export-open"]'))) return;
  close();
};
const onKey = (e: KeyboardEvent): void => {
  if (e.key !== 'Escape') return;
  // 捕获阶段先吃掉 Esc：否则同一次 Esc 会被全局热键拿去清空选中
  e.stopPropagation();
  close();
};
const onResize = (): void => position();

watch(
  () => ui.exportOpen,
  (open) => {
    if (open) {
      position();
      document.addEventListener('pointerdown', onDocDown, true);
      document.addEventListener('keydown', onKey, true);
      window.addEventListener('resize', onResize);
    } else {
      document.removeEventListener('pointerdown', onDocDown, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
      message.value = '';
      failed.value = false;
    }
  },
);

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocDown, true);
  document.removeEventListener('keydown', onKey, true);
  window.removeEventListener('resize', onResize);
});

/** 位图：倍率 / 物理尺寸 × DPI 两套参数都交给 planRaster（在 useEditor 里） */
const exportRasterNow = (kind: RasterKind): Promise<{ ok: boolean; message: string }> =>
  editor.exportRaster({
    format: kind,
    scale: sizeMode.value === 'scale' ? scale.value : undefined,
    dpi: sizeMode.value === 'dpi' ? dpi.value : undefined,
    mmWidth: sizeMode.value === 'dpi' ? mmWidth.value : undefined,
    background: bgValue.value,
    quality: quality.value,
  });

const run = async (): Promise<void> => {
  message.value = '';
  failed.value = false;
  if (format.value === 'svg') {
    editor.download();
    close();
    return;
  }
  if (format.value === 'project') {
    editor.exportProject();
    close();
    return;
  }

  // 走到这里只剩位图与 PDF：位图走光栅化，PDF 走矢量转换（jsPDF + svg2pdf 动态加载）
  const kind = rasterFormat.value;
  busy.value = true;
  const res = kind ? await exportRasterNow(kind) : await editor.exportPdf({ page: pdfPage.value });
  busy.value = false;
  if (res.ok) close();
  else {
    message.value = res.message;
    failed.value = true;
  }
};
</script>

<template>
  <Teleport to="body">
    <div v-if="ui.exportOpen" class="export-panel" :style="style" data-field="export-panel" role="dialog" aria-label="导出">
      <div class="export-row">
        <span class="panel-note">导出什么</span>
        <span class="grow" />
        <button class="ghost-btn" data-field="export-close" title="关闭" @click="close">✕</button>
      </div>

      <div class="seg export-formats">
        <button data-field="export-fmt-svg" :class="{ 'is-active': format === 'svg' }" @click="format = 'svg'">SVG</button>
        <button data-field="export-fmt-png" :class="{ 'is-active': format === 'png' }" @click="format = 'png'">PNG</button>
        <button data-field="export-fmt-jpeg" :class="{ 'is-active': format === 'jpeg' }" @click="format = 'jpeg'">JPEG</button>
        <button data-field="export-fmt-pdf" :class="{ 'is-active': isPdf }" @click="format = 'pdf'">PDF</button>
        <button data-field="export-fmt-project" :class="{ 'is-active': format === 'project' }" @click="format = 'project'">工程</button>
      </div>
      <p class="panel-note export-note">{{ formatLabel[format] }}</p>

      <template v-if="rasterFormat">
        <div class="seg">
          <button data-field="export-size-scale" :class="{ 'is-active': sizeMode === 'scale' }" @click="sizeMode = 'scale'">倍率</button>
          <button data-field="export-size-dpi" :class="{ 'is-active': sizeMode === 'dpi' }" @click="sizeMode = 'dpi'">物理尺寸 × DPI</button>
        </div>

        <template v-if="sizeMode === 'scale'">
          <div class="seg">
            <button v-for="s in [1, 2, 4]" :key="s" :data-field="`export-scale-${s}`" :class="{ 'is-active': scale === s }" @click="scale = s">
              {{ s }}×
            </button>
          </div>
        </template>
        <template v-else>
          <div class="export-field">
            <label>宽</label>
            <input v-model.number="mmWidth" class="input" type="number" min="20" max="500" step="5" data-field="export-mm" />
            <span class="panel-note">mm</span>
          </div>
          <div class="seg">
            <button v-for="d in [150, 300, 600]" :key="d" :data-field="`export-dpi-${d}`" :class="{ 'is-active': dpi === d }" @click="dpi = d">
              {{ d }} dpi
            </button>
          </div>
        </template>

        <div class="export-out">
          <div data-field="export-size">→ <b>{{ sizeText }}</b></div>
          <div class="panel-note">{{ physicalText }}</div>
          <div v-if="plan.clamped" class="export-warn" data-field="export-clamp">
            超出浏览器 canvas 上限（{{ MAX_RASTER_PX }}px），已等比缩小到 {{ sizeText }}
          </div>
        </div>

        <div class="export-row">
          <label class="panel-note">背景</label>
          <span class="grow" />
          <button
            class="ghost-btn"
            data-field="export-bg-transparent"
            :class="{ 'is-active': transparent && !isJpeg }"
            :disabled="isJpeg"
            :title="isJpeg ? 'JPEG 没有透明通道，必须铺底色' : '透明背景（PNG）'"
            @click="transparent = true"
          >
            透明
          </button>
          <input v-model="bgColor" class="pal-colorinput" type="color" data-field="export-bg-color" aria-label="背景色" @input="transparent = false" />
        </div>

        <div v-if="isJpeg" class="export-field">
          <label class="panel-note">质量</label>
          <SliderField name="export-quality" :value="Math.round(quality * 100)" :min="50" :max="100" @change="quality = $event / 100" />
          <span class="panel-note">{{ Math.round(quality * 100) }}%</span>
        </div>
      </template>

      <template v-else-if="isPdf">
        <div class="seg">
          <button
            v-for="p in PDF_PAGES"
            :key="p.id"
            :data-field="`export-pdf-page-${p.id}`"
            :class="{ 'is-active': pdfPage === p.id }"
            :title="p.hint"
            @click="pdfPage = p.id"
          >
            {{ p.label }}
          </button>
        </div>
        <div class="export-out">
          <div class="panel-note">{{ pdfHint }}</div>
          <div class="panel-note">文字可选中、放大不糊；含中文时自动内嵌字体</div>
        </div>
      </template>

      <div v-if="rasterFormat || isPdf" class="panel-note export-file" data-field="export-file">文件名：{{ fileName }}</div>

      <p v-if="message" class="export-err" :class="{ 'is-error': failed }" data-field="export-msg">{{ message }}</p>

      <button class="primary-btn export-go" data-field="export-go" :disabled="busy || !editor.svg.value" @click="run">
        {{ busy ? '导出中…' : '导出' }}
      </button>
    </div>
  </Teleport>
</template>
